import { get, times } from "lodash"
import createModule from "../createModule"
import view from "./view"
import { Creators as DialogCreators } from "modules/dialog/model"
import { Creators as EditExperimentCreators } from "modules/edit-experiment/model"
import { Creators } from "./model"
import { Creators as PortfolioRootCreators } from "modules/portfolio-root/model"
import { getBasePositionSet, getBaseSignature } from "selectors/base"
import { getViewType, getPortfolioId, getSelectedModelId, getPeriod, getCurrency } from "modules/portfolio-root/selectors/portfolios"
import { computeSecuritiesQueries, computeSecuritiesDescriptorsQueries, getCoveragePositionSetQuery } from "helpers/comparison"
import { getEarliestDate, getEndDate, getLatestDate, getSelectedDate, getFrontierDate, getStartDate, getNonOverlappingItems, getFrontierDateAdjusted } from "./selectors/dates"
import { getComparisonItems, getComparisonSecurities, getSavedCompositionGroup, getSavedMetrics, getSelectedComparisonType, getRawComparisonItems, getExperimentToUpdate, SEARCH_ITEMS_ACTIONS, getBaseUrl, getComparisonTypes, getModels, getExperimentToConvert } from "./selectors"
import { getContentSets } from "selectors/contentSets"
import { getIsOpPro, getIsIntradayAccountEnabled } from "modules/account-root/selectors"
import { MODELS_QUERY } from "resources/models"
import { BASKETS_QUERY } from "resources/baskets"

export default createModule({
  view,
  externalProps: state => ({
    account: state.global.selected.account,
    accountId: state.global.selected.accountId,
    modelId: getSelectedModelId(state),
    startDate: getStartDate(state),
    endDate: getEndDate(state),
    latestDate: getLatestDate(state),
    frontierDate: getFrontierDate(state),
    viewType: getViewType(state),
    base: getBasePositionSet(state),
    comparisonSecurities: getComparisonSecurities(state),
    selectedComparisonType: getSelectedComparisonType(state),
    baseSignature: getBaseSignature(state),
    portfolioUUID: getPortfolioId(state),
    rawComparisonItems: getRawComparisonItems(state),
    comparisonItems: getComparisonItems(state),
  }),
  data: [{
    resource: "benchmarksAndPortfolios",
    as: null,
    url: ({ modelId }) => {
      if (modelId) {
        return ({
          id: `benchmarksAndPortfolios-${modelId}`,
          query: `query ($modelId: String!) {
            model (id: $modelId) {
              benchmarks { id, name, availableFrom }
            }
            portfolios { id, name, availableFrom }
          }`,
          variables: { modelId },
        })
      } else {
        return null
      }
    },
  }, {
    ...BASKETS_QUERY,
    as: null,
  }, {
    resource: "securities",
    as: null,
    passthrough: true,
    url: ({ modelId, comparisonSecurities }) => {
      if (modelId && comparisonSecurities.length > 0) {
        const securitiesQueries = computeSecuritiesQueries(comparisonSecurities)

        return ({
          id: `securities-${modelId}-${securitiesQueries}`,
          query: `query ($modelId: String!) {
            model (id: $modelId) {
              ${securitiesQueries}
            }
          }`,
          variables: { modelId },
        })
      } else {
        return null
      }
    },
  }, {
    resource: "securitiesDescriptors",
    as: null,
    passthrough: true,
    url: ({ modelId, comparisonSecurities, endDate, selectedComparisonType, frontierDate }) => {
      if (modelId && comparisonSecurities.length > 0) {
        const securitiesDescriptorsQueries = computeSecuritiesDescriptorsQueries(comparisonSecurities)
        const date = selectedComparisonType.id !== "frontier" ? endDate : frontierDate

        return ({
          id: `securities-${modelId}-${securitiesDescriptorsQueries}-${date}`,
          query: `query ($modelId: String!, $on: Date!) {
            model (id: $modelId) {
              ${securitiesDescriptorsQueries}
            }
          }`,
          variables: { modelId, on: date },
        })
      } else {
        return null
      }
    },
  },
  {
    resource: "experimentsPreferences",
    url: ({ accountId, portfolioUUID }) => {
      if (!portfolioUUID) {
        return null
      }

      return ({
        id: `experimentsPreferences-${accountId}${portfolioUUID}`,
        url: "/api/users/me/preferences",
        payload: {
          items: [
            {
              type: "experimentSettings",
              key: `${portfolioUUID}`,
              accountId,
            },
          ],
        },
      })
    },
  }, ...times(4, (n) => {
    return {
      resource: `experimentItemCoverage${n + 1}`, // To match the other queries by items, we don't get the base portfolio here
      as: null,
      url: ({ modelId, rawComparisonItems, startDate, endDate, portfolioUUID }) => {
        if (!rawComparisonItems[n] || rawComparisonItems[n].uploading) {
          return null
        }
        const positionSet = getCoveragePositionSetQuery(rawComparisonItems[n], startDate, portfolioUUID)

        return {
          id: `${modelId}${JSON.stringify(positionSet)}${startDate}${endDate}`,
          query: `query ($modelId: String!, $positionSet: PositionSetInput!, $from: Date!, $to: Date!) {
            model (id: $modelId) {
              simulation (positionSet: $positionSet,from: $from, to: $to) {
                coverage{
                  date
                  summary{
                    percentGmvAvailable
                  }
                }
              }
            }
          }`,
          variables: {
            modelId,
            positionSet,
            from: startDate,
            to: endDate,
          },
        }
      },
    }
  }), {
    ...MODELS_QUERY,
    as: null,
  }],
  mapStateToProps: (state, props) => ({
    comparisonItems: getComparisonItems(state),
    period: getPeriod(state),
    earliestDate: getEarliestDate(state),
    savedMetrics: getSavedMetrics(state),
    savedCompositionGroup: getSavedCompositionGroup(state),
    nonOverlappingItems: getNonOverlappingItems(state),
    searchItemsActions: SEARCH_ITEMS_ACTIONS,
    portfolio: state.global.selected.portfolio,
    tradesPanelOpen: get(state, "modules.experiments.tradesPanelOpen"),
    selectedExperiment: get(state, "modules.experiments.selected"),
    frontierDateAdjusted: getFrontierDateAdjusted(state),
    selectedDate: getSelectedDate(state),
    experimentToUpdate: getExperimentToUpdate(state),
    baseUrl: getBaseUrl(state, props),
    contentSets: getContentSets(state),
    comparisonTypes: getComparisonTypes(state),
    models: getModels(state),
    experimentToConvert: getExperimentToConvert(state),
    isOpPro: getIsOpPro(state),
    intradayAccountEnabled: getIsIntradayAccountEnabled(state, props),
    portfolioCurrency: getCurrency(state),
  }),
  actions: {
    ...Creators,
    openDialog: DialogCreators.open,
    loadEditExperimentIndex: EditExperimentCreators.loadIndex,
    setSelectedDate: PortfolioRootCreators.setDate,
    resetSelectedDate: PortfolioRootCreators.resetDate,
    setPeriod: PortfolioRootCreators.setPeriod,
  },
})
